// OP-3605 — Baixas (ver ESL1; decidir ESL14; anexar ESL18): a fila de peças
// que alguém propôs tirar do patrimônio. Propor é na tela do equipamento;
// aqui se decide — e a decisão é da gerência, com anexo opcional como prova.
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueBaixas, useEstoqueBaixasSemProposta } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { createEstoqueAnexoUrl, decidirEstoqueBaixa } from '../../../../Service/estoque'
import { AnexoEnviado, BaixaRow, BaixaSemPropostaRow, BaixasFilters, StatusBaixa, lugarDoLocal } from '../../../../types/estoque'
import {
  BarraChips, BD, SEVERIDADE, Button, CaixaLista, Card, Chip, Field, FiltersRow, Folha, FolhaOverlay, FolhaPega,
  FolhaTopo, Input, ListaCartoes, PageSubtitle, PageTitle, PainelLateral, Select, SoCelular, SoDesktop, Spinner,
  StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { AnexarArquivo } from '../AnexarArquivo'
import { tratarErro } from '../erros'
import { statusDaUrl } from '../filtrosUrl'
import { plural } from '../formato'
import { RefLink } from '../RefLink'
import { CICLO_UI, CONDICAO_UI } from '../Equipamento/regras'
import { STATUS_BAIXA_UI, impedimentoAnexo, impedimentoAprovarBaixa, impedimentoRecusarBaixa } from './regras'
import { useModal } from '../useModal'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const STATUS: StatusBaixa[] = ['PROPOSTA', 'APROVADA', 'RECUSADA']
const COLS = 6
const COLS_SEM_PROPOSTA = 6

export const EstoqueBaixas: FC = () => {
  const navigate = useNavigate()
  const { pode, usuarioId } = usePerfilEstoque()
  const podeDecidir = pode('DECIDIR_BAIXA')
  const podeAnexar = pode('ANEXAR')
  const [searchParams] = useSearchParams()
  const { hash } = useLocation()
  const statusUrl = statusDaUrl(searchParams, STATUS)
  const [draft, setDraft] = useState<BaixasFilters>({ status: statusUrl ?? 'PROPOSTA', page: 1 })
  const [applied, setApplied] = useState<BaixasFilters>({ status: statusUrl ?? 'PROPOSTA', page: 1 })
  const { rows, total, lastPage, isLoading, isError, isUnauthorized, mutate } = useEstoqueBaixas(applied)
  const semPropostaQ = useEstoqueBaixasSemProposta()

  useEffect(() => {
    if (!statusUrl) return
    setDraft((d) => ({ ...d, status: statusUrl, page: 1 }))
    setApplied((a) => ({ ...a, status: statusUrl, page: 1 }))
  }, [statusUrl])
  useEffect(() => {
    if (hash !== '#sem-proposta' || semPropostaQ.isLoading) return
    document.getElementById('sem-proposta')?.scrollIntoView({ block: 'start' })
  }, [hash, semPropostaQ.isLoading])
  const [decidindo, setDecidindo] = useState<BaixaRow | null>(null)
  const [motivo, setMotivo] = useState('')
  const [anexos, setAnexos] = useState<AnexoEnviado[]>([])
  const [busy, setBusy] = useState(false)
  const pagina = Math.max(1, Number(applied.page) || 1)
  const painel = useRef<HTMLDivElement>(null)
  const folha = useRef<HTMLDivElement>(null)
  const temRascunho = !!motivo.trim() || anexos.length > 0

  const fechar = useCallback(() => { setDecidindo(null); setMotivo(''); setAnexos([]) }, [])
  const fecharSemRascunho = useCallback(() => { if (!busy && !temRascunho) fechar() }, [busy, temRascunho, fechar])

  useModal(!!decidindo, [painel, folha], fecharSemRascunho)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const enviarAnexo = async (arquivo: File) => {
    if (!decidindo) return
    const imp = impedimentoAnexo(arquivo, anexos.length)
    if (imp) { toast.error(imp); return }
    setBusy(true)
    try {
      const { data } = await createEstoqueAnexoUrl('BAIXA', decidindo.id, arquivo.type)
      const put = await fetch(data.data.url, { method: 'PUT', headers: { 'Content-Type': arquivo.type }, body: arquivo })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      setAnexos((a) => [...a, { key: data.data.key, nome: arquivo.name, mime: arquivo.type, bytes: arquivo.size }])
      toast.success('Anexo pronto para entrar com a decisão')
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
    } finally {
      setBusy(false)
    }
  }

  const decidir = async (aprovar: boolean) => {
    if (!decidindo) return
    setBusy(true)
    try {
      await decidirEstoqueBaixa(decidindo.id, aprovar, motivo.trim() || undefined, anexos.length ? anexos : undefined)
      toast.success(aprovar ? 'Baixa aprovada' : 'Proposta recusada')
      fechar()
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  const linha = (b: BaixaRow) => {
    const ui = STATUS_BAIXA_UI[b.status] ?? { bg: '#F3F4F6', color: '#374151', label: b.status }
    const cond = CONDICAO_UI[b.condicao]
    const ciclo = CICLO_UI[b.cicloVida]
    return (
      <tr key={b.id}>
        <td><StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></td>
        <td>
          <b>{b.itemNome}</b>
          <div style={{ fontSize: 12 }}>
            <a href={`/interno/estoque/equipamentos/${b.unidadeId}`} style={{ fontFamily: 'monospace' }}
               onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/equipamentos/${b.unidadeId}`) }}>{b.serial}</a>
            <span style={{ color: BD.grayText }}> · {b.pracaNome}</span>
          </div>
        </td>
        <td>
          {cond && <StatusBadge $bg={cond.bg} $color={cond.color}>{cond.label}</StatusBadge>}{' '}
          {ciclo && b.cicloVida !== 'ATIVO' && <StatusBadge $bg={ciclo.bg} $color={ciclo.color}>{ciclo.label}</StatusBadge>}
        </td>
        <td style={{ fontSize: 13 }}>{b.motivo}<div style={{ fontSize: 11, color: BD.grayText }}>{b.propostoPorNome} · {fmtDateTime(b.propostoEm)}</div></td>
        <td style={{ fontSize: 13 }}>
          {b.decididoPorNome ? <>{b.decididoPorNome}<div style={{ fontSize: 11, color: BD.grayText }}>{fmtDateTime(b.decididoEm)}{b.motivoDecisao ? ` · ${b.motivoDecisao}` : ''}</div></> : '—'}
        </td>
        <td>
          {b.status === 'PROPOSTA' && podeDecidir && (
            <Button $variant="secondary" disabled={busy} onClick={() => { setDecidindo(b); setMotivo(''); setAnexos([]) }}>Decidir</Button>
          )}
        </td>
      </tr>
    )
  }

  /**
   * Anexar é capacidade da decisão, não do desktop: a folha do celular usa o
   * mesmo bloco. Sem isto, quem decide no aparelho perderia o comprovante.
   */
  const blocoAnexos = () => (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Anexos (opcional)</div>
      {!podeAnexar ? (
        <div style={{ fontSize: 12, color: BD.grayText }}>Anexar exige a permissão "Anexos". A decisão pode ser registrada sem anexo.</div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>Até 5 arquivos, imagem ou PDF, 8 MB cada. Eles entram na mesma transação da decisão: ou entram todos, ou nada.</div>
          <AnexarArquivo aoEnviar={enviarAnexo} disabled={busy} />
          {anexos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {anexos.map((a) => (
                <span key={a.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', border: `1px solid ${BD.grayBorder}`, borderRadius: 12 }}>
                  {a.nome}
                  <button type="button" aria-label={`Remover ${a.nome}`} disabled={busy}
                          onClick={() => setAnexos((lista) => lista.filter((x) => x.key !== a.key))}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: BD.grayText, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )

  const escolherStatus = (status: StatusBaixa | '') => {
    setDraft((d) => ({ ...d, status, page: 1 }))
    setApplied((a) => ({ ...a, status, page: 1 }))
  }

  const cartao = (b: BaixaRow) => {
    const ui = STATUS_BAIXA_UI[b.status] ?? { bg: '#F3F4F6', color: '#374151', label: b.status }
    const aDecidir = b.status === 'PROPOSTA'
    return (
      <Card key={b.id} style={{ padding: 14, borderColor: aDecidir ? SEVERIDADE.atencao.borda : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flexGrow: 1, fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{b.serial}</span>
          <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge>
        </div>
        <div style={{ marginTop: 6, fontSize: 16, fontWeight: 600 }}>{b.itemNome}</div>
        <div style={{ marginTop: 2, fontSize: 13, color: BD.grayText }}>{b.pracaNome} · proposta por {b.propostoPorNome} em {fmtDate(b.propostoEm)}</div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BD.grayBorder}`, fontSize: 13 }}>
          {b.motivo}
          {b.decididoPorNome && (
            <div style={{ marginTop: 4, color: BD.grayText }}>
              {b.decididoPorNome} · {fmtDate(b.decididoEm)}{b.motivoDecisao ? ` · ${b.motivoDecisao}` : ''}
            </div>
          )}
        </div>
        {aDecidir && podeDecidir && (
          <Button style={{ marginTop: 12, width: '100%' }} disabled={busy} onClick={() => { setDecidindo(b); setMotivo(''); setAnexos([]) }}>
            Decidir a baixa
          </Button>
        )}
      </Card>
    )
  }

  const impAprovar = decidindo ? impedimentoAprovarBaixa(decidindo, podeDecidir, usuarioId) : null
  const impRecusar = decidindo ? impedimentoRecusarBaixa(decidindo, podeDecidir, motivo, usuarioId) : null

  const conteudoDecisao = (b: BaixaRow) => (
    <>
      <div style={{ fontFamily: 'monospace', fontSize: 13, color: BD.grayText }}>{b.serial}</div>
      <div style={{ marginTop: 2, fontSize: 16, fontWeight: 600 }}>{b.itemNome}</div>
      <div style={{ marginTop: 10, padding: 12, background: SEVERIDADE.critico.bg, border: `1px solid ${SEVERIDADE.critico.borda}`, borderRadius: 10, fontSize: 13, lineHeight: '19px' }}>
        Aprovar retira a peça do patrimônio de forma definitiva — a baixa não se estorna.
        <div style={{ marginTop: 6, color: BD.grayText }}>Proposta por {b.propostoPorNome}: “{b.motivo}”.</div>
      </div>
      <Field style={{ marginTop: 12 }}>
        <label>Motivo da decisão (obrigatório para recusar)</label>
        <Input value={motivo} maxLength={400} onChange={(e) => setMotivo(e.target.value)} />
      </Field>
      {blocoAnexos()}
      {(impAprovar || impRecusar) && (
        <div style={{ marginTop: 10, fontSize: 13, color: BD.grayText }}>{impAprovar || impRecusar}</div>
      )}
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button disabled={busy || !!impAprovar} onClick={() => decidir(true)}>Aprovar baixa</Button>
        <Button $variant="secondary" disabled={busy || !!impRecusar} onClick={() => decidir(false)}>Recusar</Button>
        <Button $variant="secondary" disabled={busy} onClick={fechar}>Fechar</Button>
      </div>
    </>
  )

  const irPeca = (e: any, unidadeId: number) => { e.preventDefault(); navigate(`/interno/estoque/equipamentos/${unidadeId}`) }
  const linhaSemProposta = (p: BaixaSemPropostaRow) => {
    const cond = CONDICAO_UI[p.condicao]
    const ciclo = CICLO_UI[p.cicloVida]
    return (
      <tr key={p.unidadeId}>
        <td>
          <a href={`/interno/estoque/equipamentos/${p.unidadeId}`} style={{ fontFamily: 'monospace', fontWeight: 600 }} onClick={(e) => irPeca(e, p.unidadeId)}>{p.serial}</a>
          <div style={{ fontSize: 11, color: BD.grayText, fontFamily: 'monospace' }}>{p.patrimonio}</div>
        </td>
        <td>{p.itemNome}</td>
        <td style={{ fontSize: 13 }}>{p.pracaNome}{p.localNome && p.localNome !== p.pracaNome && <div style={{ fontSize: 11, color: BD.grayText }}>está em {lugarDoLocal(p.localNome, p.localTipo)}</div>}</td>
        <td>
          {ciclo && p.cicloVida !== 'ATIVO' && <StatusBadge $bg={ciclo.bg} $color={ciclo.color}>{ciclo.label}</StatusBadge>}{' '}
          {cond && <StatusBadge $bg={cond.bg} $color={cond.color}>{cond.label}</StatusBadge>}
        </td>
        <td style={{ fontSize: 13 }}>{fmtDate(p.desde)}</td>
        <td style={{ fontSize: 13 }}>{p.referenciaId ? <RefLink tipo="" codigo={p.referenciaId} /> : <span style={{ color: BD.grayText }}>—</span>}</td>
      </tr>
    )
  }

  return (
    <>
      <PageTitle>Baixas</PageTitle>
      <PageSubtitle>
        Tirar uma peça do patrimônio é decisão da gerência, e só vale para o que já está perdido ou imprestável. Propor é na tela do equipamento; aprovar lança a baixa no razão e não se desfaz por estorno.
      </PageSubtitle>

      <BarraChips role="group" aria-label="Status da baixa">
        <Chip type="button" $ativo={!applied.status} onClick={() => escolherStatus('')}>Todas</Chip>
        {STATUS.map((s) => (
          <Chip key={s} type="button" $ativo={applied.status === s} onClick={() => escolherStatus(s)}>
            {STATUS_BAIXA_UI[s].label}
          </Chip>
        ))}
      </BarraChips>

      <SoDesktop>
        <FiltersRow>
          <Field>
            <label>Status</label>
            <Select value={draft.status ?? ''} onChange={(e) => setDraft({ ...draft, status: e.target.value as StatusBaixa | '' })}>
              <option value="">todas</option>
              {STATUS.map((s) => <option key={s} value={s}>{STATUS_BAIXA_UI[s].label}</option>)}
            </Select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}><Button $variant="secondary" onClick={() => setApplied({ ...draft, page: 1 })}>Aplicar</Button></div>
        </FiltersRow>
      </SoDesktop>

      {decidindo && (
        <>
          <SoDesktop>
            <FolhaOverlay $open onClick={fecharSemRascunho} />
            <PainelLateral ref={painel} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Decidir a baixa">
              <FolhaTopo>
                <h2>Decidir a baixa</h2>
              </FolhaTopo>
              {conteudoDecisao(decidindo)}
            </PainelLateral>
          </SoDesktop>
          <SoCelular>
            <FolhaOverlay $open onClick={fecharSemRascunho} />
            <Folha ref={folha} tabIndex={-1} $open role="dialog" aria-modal="true" aria-label="Decidir a baixa">
              <FolhaPega />
              <FolhaTopo>
                <h2>Decidir a baixa</h2>
              </FolhaTopo>
              {conteudoDecisao(decidindo)}
            </Folha>
          </SoCelular>
        </>
      )}

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{isLoading ? 'Carregando…' : `${total} proposta${total === 1 ? '' : 's'}`}</div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Status</th><th>Peça</th><th>Estado</th><th>Proposta</th><th>Decisão</th><th /></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                  Não foi possível carregar as baixas. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                </td></tr>
              )}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhuma proposta com os filtros atuais.</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && (
            <Card style={{ padding: 20, color: BD.red }}>
              Não foi possível carregar as baixas. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
            </Card>
          )}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>Nenhuma proposta com os filtros atuais.</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={pagina <= 1} onClick={() => setApplied((a) => ({ ...a, page: pagina - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {pagina}</span>
          <Button $variant="secondary" disabled={!!isError || isLoading || !!lastPage} onClick={() => setApplied((a) => ({ ...a, page: pagina + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>

      <CaixaLista id="sem-proposta" style={{ marginTop: 16 }}>
        <div style={{ padding: '12px 12px 4px' }}>
          <div style={{ fontWeight: 600 }}>Extraviadas sem proposta</div>
          <div style={{ fontSize: 13, color: BD.grayText, marginTop: 4 }}>
            Peças declaradas perdidas ou sem conserto que ainda estão no patrimônio e não têm proposta de baixa. Propor é ato na ficha da peça.
          </div>
        </div>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>
          {semPropostaQ.isLoading ? 'Carregando…' : semPropostaQ.isError ? '—' : plural(semPropostaQ.total, 'peça', 'peças')}
        </div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Peça</th><th>Item</th><th>Praça / onde está</th><th>Situação</th><th>Desde</th><th>Documento do extravio</th></tr></thead>
            <tbody>
              {semPropostaQ.isLoading && <tr><td colSpan={COLS_SEM_PROPOSTA} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!semPropostaQ.isLoading && semPropostaQ.isError && (
                <tr><td colSpan={COLS_SEM_PROPOSTA} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                  Não foi possível carregar as peças sem proposta. <Button $variant="secondary" onClick={() => semPropostaQ.mutate()}>Tentar de novo</Button>
                </td></tr>
              )}
              {!semPropostaQ.isLoading && !semPropostaQ.isError && semPropostaQ.rows.length === 0 && (
                <tr><td colSpan={COLS_SEM_PROPOSTA} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhuma peça esperando proposta.</td></tr>
              )}
              {!semPropostaQ.isLoading && !semPropostaQ.isError && semPropostaQ.rows.map(linhaSemProposta)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {semPropostaQ.isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!semPropostaQ.isLoading && semPropostaQ.isError && (
            <Card style={{ padding: 20, color: BD.red }}>
              Não foi possível carregar as peças sem proposta. <Button $variant="secondary" onClick={() => semPropostaQ.mutate()}>Tentar de novo</Button>
            </Card>
          )}
          {!semPropostaQ.isLoading && !semPropostaQ.isError && semPropostaQ.rows.length === 0 && (
            <Card style={{ padding: 20, color: BD.grayText }}>Nenhuma peça esperando proposta.</Card>
          )}
          {!semPropostaQ.isLoading && !semPropostaQ.isError && semPropostaQ.rows.map((u) => (
            <Card key={u.unidadeId} style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flexGrow: 1, fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{u.serial}</span>
                <StatusBadge $bg={SEVERIDADE.critico.bg} $color={SEVERIDADE.critico.texto}>{CICLO_UI[u.cicloVida]?.label ?? u.cicloVida}</StatusBadge>
              </div>
              <div style={{ marginTop: 6, fontSize: 16, fontWeight: 600 }}>{u.itemNome}</div>
              <div style={{ marginTop: 2, fontSize: 13, color: BD.grayText }}>{u.pracaNome}{u.localNome ? ` · ${u.localNome}` : ''}</div>
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BD.grayBorder}`, fontSize: 13, color: BD.grayText }}>
                {u.desde ? `extraviada desde ${fmtDate(u.desde)} · ` : ''}
                {u.referenciaId ? `${u.referenciaId} · ` : ''}
                propor a baixa é ato na ficha da peça
              </div>
            </Card>
          ))}
        </ListaCartoes>
      </CaixaLista>
    </>
  )
}

export default EstoqueBaixas
